import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Icon,
    IconButton,
    TextField,
    Tooltip,
} from '@mui/material';
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React from 'react';
import { connect } from 'react-redux';

import { Mcc } from 'app/model/cris/mcc';
import { RootState } from 'app/store';

import { searchMccData, selectMccLookupData, selectMccLookupShown, setMccLookupVisible } from './slice';

interface MccLookupProps {
    show: boolean;
    data?: Mcc[];

    setMccLookupVisible: (visible: boolean) => void;

    searchMccData: (search: string) => void;
}

class MccLookup extends React.Component<MccLookupProps> {
    public render() {
        const columns: Array<GridColDef<Mcc>> = [
            {
                field: 'id',
                headerName: 'MCC',
                width: 150,
                renderCell: (params: GridRenderCellParams<Mcc, Mcc['id']>) => (
                    <React.Fragment>
                        {params.value}
                        <Tooltip title='Copy to Clipboard' placement='right'>
                            <IconButton
                                className='show-on-hover'
                                onClick={() => {
                                    void navigator.clipboard.writeText(params.value ?? '');
                                }}
                                size='small'
                            >
                                <Icon fontSize='small'>content_copy</Icon>
                            </IconButton>
                        </Tooltip>
                    </React.Fragment>
                ),
            },
            {
                field: 'name',
                headerName: 'Name',
                width: 300,
                flex: 1,
            },
        ];

        return (
            <Dialog
                open={this.props.show}
                onClose={() => this.props.setMccLookupVisible(false)}
                aria-labelledby='mmc-lookup-dialog-title'
                aria-describedby='mmc-lookup-dialog-description'
                fullWidth
                maxWidth='lg'
            >
                <DialogTitle id='mmc-lookup-dialog-title'>MCC Lookup Tool</DialogTitle>
                <DialogContent>
                    <DialogContentText id='mmc-lookup-dialog-description'>
                        Type in MMC value or part of the name to find corresponding records.
                    </DialogContentText>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        <Icon sx={{ color: 'action.active', mr: 1, my: 0.6 }}>search</Icon>
                        <TextField
                            autoFocus
                            margin='dense'
                            id='mcc-query'
                            label='Search Query'
                            fullWidth
                            variant='standard'
                            onKeyDown={(evt) => this.handleKey(evt.key, (evt.target as HTMLInputElement).value)}
                        />
                    </Box>
                    <Box sx={{ height: '60vh' }}>
                        <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                            <DataGridPro
                                columns={columns}
                                rows={this.props.data || []}
                                loading={undefined === this.props.data}
                                pagination
                                density='compact'
                                autoPageSize
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => this.props.setMccLookupVisible(false)} variant='contained' color='primary'>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    private handleKey(key: string, value: string) {
        if ('Enter' !== key) {
            return;
        }

        this.props.searchMccData(value);
    }
}

const mapStateToProps = (state: RootState) => ({
    show: selectMccLookupShown(state),
    data: selectMccLookupData(state),
});

const actions = {
    setMccLookupVisible: setMccLookupVisible as ActionCreatorWithPayload<boolean>,
    searchMccData,
};

const enhanced = connect(mapStateToProps, actions)(MccLookup);

export { enhanced as MccLookup };
